import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { verifyEmail, verifyEmailCode, resendVerification } from '../store/slices/authSlice';
import { toast } from 'react-toastify';

const MailIcon = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  // 'verifying' only covers the emailed-link path; every other outcome lands on
  // the code form so the user is never stranded.
  const [status, setStatus] = useState(token ? 'verifying' : 'code');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    dispatch(verifyEmail(token)).then((result) => {
      if (verifyEmail.fulfilled.match(result)) {
        toast.success('Email verified — welcome to MyCarCollection!');
        navigate('/');
      } else {
        // A dead or expired link is recoverable — the same email carries a
        // 6-digit code, so drop to the code form rather than dead-ending.
        setErrorMsg(result.payload || 'That verification link is no longer valid.');
        setStatus('code');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim();
    const cleanCode = code.replace(/\D/g, '');
    if (!cleanEmail) { setErrorMsg('Enter the email you signed up with.'); return; }
    if (cleanCode.length !== 6) { setErrorMsg('Enter the 6-digit code from your email.'); return; }

    setSubmitting(true);
    setErrorMsg('');
    const result = await dispatch(verifyEmailCode({ email: cleanEmail, code: cleanCode }));
    setSubmitting(false);

    if (verifyEmailCode.fulfilled.match(result)) {
      toast.success('Email verified — welcome to MyCarCollection!');
      navigate('/');
      return;
    }

    const payload = result.payload || {};
    if (payload.code === 'already_verified') {
      toast.info('That email is already verified — please sign in.');
      navigate('/login');
      return;
    }
    setErrorMsg(payload.message || 'Verification failed.');
    // A spent code is worth clearing so the field doesn't look re-submittable.
    if (payload.code === 'code_expired' || payload.code === 'too_many_attempts') setCode('');
  };

  const resend = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail) { setErrorMsg('Enter your email first, then request a new code.'); return; }
    setResending(true);
    const result = await dispatch(resendVerification(cleanEmail));
    setResending(false);
    if (resendVerification.fulfilled.match(result)) {
      toast.success('If that email is registered and unverified, a new code is on its way.');
      setErrorMsg('');
      setCode('');
    } else {
      setErrorMsg(result.payload || 'Could not send a new code.');
    }
  };

  return (
    <>
      <div className="auth-bg-fixed" style={{ backgroundImage: "url('/images/Background.png')" }} />
      <div className="auth-page-wrap">
        <section className="auth-hero">
          <div className="auth-hero-brand">
            <div className="auth-brand-wordmark">
              <span className="auth-brand-wordmark-text"><span className="wm">MyCarCollection</span></span>
            </div>
          </div>
          <div className="auth-hero-card">
            <div className="auth-glass-card" style={{ padding: '40px 32px' }}>

              {status === 'verifying' && (
                <div style={{ textAlign: 'center' }}>
                  <div className="loading-spinner" style={{ marginBottom: 20 }} />
                  <h2 className="auth-form-title">Verifying…</h2>
                  <p style={{ color: '#8892b0', fontSize: '0.9rem' }}>Just a moment.</p>
                </div>
              )}

              {status === 'code' && (
                <>
                  <div style={{ textAlign: 'center', color: '#c9ced6', marginBottom: 16 }}>
                    <MailIcon />
                  </div>
                  <h2 className="auth-form-title" style={{ textAlign: 'center', marginBottom: 8 }}>
                    Confirm your email
                  </h2>
                  <p style={{ color: '#8892b0', fontSize: '0.9rem', textAlign: 'center', marginBottom: 24 }}>
                    Enter the 6-digit code we emailed you. You&rsquo;ll be signed in straight away.
                  </p>

                  <form onSubmit={submitCode}>
                    <input
                      className="auth-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      aria-label="Email address"
                    />
                    <input
                      className="auth-input"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      aria-label="6-digit verification code"
                      style={{ letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.15rem' }}
                    />

                    {errorMsg && <div className="auth-error">{errorMsg}</div>}

                    <button className="auth-submit" type="submit" disabled={submitting}>
                      {submitting ? 'Verifying…' : 'Verify email'}
                    </button>
                  </form>

                  <div style={{ textAlign: 'center', marginTop: 18, fontSize: '0.85rem', color: '#8892b0' }}>
                    <button
                      type="button"
                      onClick={resend}
                      disabled={resending}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        color: '#5b9dff', cursor: resending ? 'default' : 'pointer',
                        font: 'inherit', textDecoration: 'underline',
                      }}
                    >
                      {resending ? 'Sending…' : 'Send a new code'}
                    </button>
                    <span style={{ margin: '0 8px', opacity: 0.5 }}>·</span>
                    <Link to="/login" style={{ color: '#8892b0' }}>Back to sign in</Link>
                  </div>
                </>
              )}

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VerifyEmail;
